package org.teddapp.project.ui.objectives

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import com.tedd.db.Skill
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Category
import com.tedd.db.Objective
import com.tedd.db.Task
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.ChipSize
import org.teddapp.project.ui.ChipTone
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddChip
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.ReorderableColumn
import org.teddapp.project.ui.TeddRing
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.rememberReorderableState
import org.teddapp.project.ui.teddSurface
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddTokenProgress

/**
 * Drawer de détail d'un objectif. Ouvert depuis Grille ou Cartes.
 *
 * Montre tout en mode interactif :
 *  - Header : ring + titre + catégorie + actions (modifier / supprimer)
 *  - Description + motivation
 *  - Liste des jalons avec leurs tâches cochables / supprimables / ajoutables
 *  - Réutilise `MilestoneBlock` (CRUD tâches inline) défini dans ObjectivesScreenMac.
 */
@Composable
fun ObjectiveDetailPanel(
    objective: Objective,
    category: Category?,
    repos: TeddRepositories,
    onClose: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onToggleTask: (Task) -> Unit,
    onSendToPomodoro: ((Task) -> Unit)? = null,
    skill: Skill? = null,
    onOpenSkill: ((String) -> Unit)? = null,
    onOpenMindMap: ((String) -> Unit)? = null,
) {
    val color = category?.let { runCatching { Color(parseHexDetail(it.color)) }.getOrNull() }
    val milestones by repos.milestones.observeByObjective(objective.id).collectAsState(emptyList())
    val mindMaps by repos.mindMaps.observeByObjective(objective.id).collectAsState(emptyList())
    val scope = rememberCoroutineScope()
    var confirmDeleteMilestoneId by remember { mutableStateOf<String?>(null) }

    Column(
        modifier = Modifier
            .width(560.dp)
            .fillMaxHeight()
            .background(TeddTokens.Bg)
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = {},
            ),
    ) {
        // ── Header : title bar avec close + actions ──
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(TeddTokens.Bg)
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                .padding(horizontal = 22.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Text(
                text = "DÉTAIL",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.6.sp,
                modifier = Modifier.weight(1f),
            )
            TeddButton(
                text = "Modifier",
                onClick = onEdit,
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                leadingIcon = {
                    TeddIcon(name = "edit", size = 12.dp, color = TeddTokens.Ink, strokeWidth = 1.8.dp)
                },
            )
            TeddButton(
                text = "Supprimer",
                onClick = onDelete,
                kind = TeddButtonKind.Ghost,
                size = TeddButtonSize.Sm,
                leadingIcon = {
                    TeddIcon(name = "trash", size = 12.dp, color = TeddTokens.InkMuted)
                },
            )
            TeddIconButton(onClick = onClose, size = 30.dp) {
                Box(modifier = Modifier.rotate(45f)) {
                    TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted)
                }
            }
        }

        // ── Body scrollable ──
        Column(
            modifier = Modifier
                .weight(1f)
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            // Hero : ring + titre + chip catégorie + deadline
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(18.dp)) {
                TeddRing(
                    value = objective.progress.toInt().coerceIn(0, 100),
                    size = 84.dp,
                    strokeWidth = 7.dp,
                    accent = color,
                    sub = "%",
                )
                Column(modifier = Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        if (category != null) {
                            TeddChip(
                                text = category.name,
                                color = color,
                                tone = ChipTone.Soft,
                                size = ChipSize.Sm,
                                leadingIcon = {
                                    TeddIcon(name = category.icon, size = 11.dp, color = color ?: TeddTokens.LimeStrong, strokeWidth = 2.dp)
                                },
                            )
                        }
                        val pretty = formatDateFr(objective.deadline, long = true)
                        if (pretty != null) {
                            val tint = TeddTokens.InkMuted
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                TeddIcon(name = "calendar", size = 11.dp, color = tint)
                                Text(if (isOverdue(objective.deadline)) "$pretty · échéance passée" else pretty, color = tint, fontSize = 12.sp)
                            }
                        }
                        // La compétence que cet objectif fait progresser : un tap ramène à sa fiche.
                        if (skill != null) {
                            val skillColor = runCatching { Color(parseHexDetail(skill.color)) }.getOrNull() ?: TeddTokens.LimeStrong
                            Box(
                                modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onOpenSkill?.invoke(skill.id) },
                            ) {
                                TeddChip(
                                    text = "${skill.name} →",
                                    color = skillColor,
                                    tone = ChipTone.Soft,
                                    size = ChipSize.Sm,
                                    leadingIcon = { TeddIcon(name = skill.icon, size = 11.dp, color = skillColor, strokeWidth = 2.dp) },
                                )
                            }
                        }
                        // Les cartes heuristiques rattachées, ou en créer une sur cet objectif.
                        if (onOpenMindMap != null) {
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                mindMaps.forEach { map ->
                                    Box(modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onOpenMindMap(map.id) }) {
                                        TeddChip(
                                            text = "${map.title} →",
                                            color = TeddTokens.ModNotes,
                                            tone = ChipTone.Soft,
                                            size = ChipSize.Sm,
                                            leadingIcon = { TeddIcon(name = "mindmap", size = 11.dp, color = TeddTokens.ModNotes, strokeWidth = 2.dp) },
                                        )
                                    }
                                }
                                Box(modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                                    scope.launch { onOpenMindMap(repos.mindMaps.create(objective.title, objectiveId = objective.id, skillId = objective.skill_id)) }
                                }) {
                                    TeddChip(
                                        text = if (mindMaps.isEmpty()) "Carte heuristique" else "Nouvelle carte",
                                        color = TeddTokens.InkMuted,
                                        tone = ChipTone.Soft,
                                        size = ChipSize.Sm,
                                        leadingIcon = { TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp) },
                                    )
                                }
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(6.dp))
                    Text(
                        text = objective.title,
                        style = TextStyle(
                            color = TeddTokens.Ink,
                            fontSize = 22.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = (-0.4).sp,
                        ),
                    )
                }
            }

            // Progression en dix jetons : toucher un jeton règle la valeur (surcharge manuelle).
            Column(
                modifier = Modifier.fillMaxWidth().teddSurface(shape = RoundedCornerShape(TeddTokens.RLg), elevation = 0.dp).padding(14.dp),
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(bottom = 10.dp)) {
                    Text("Progression", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
                    Text("Un jeton = 10 %", color = TeddTokens.InkMuted, fontSize = 11.sp)
                }
                TeddTokenProgress(
                    value = objective.progress.toInt().coerceIn(0, 100),
                    accent = color,
                    onChange = { next ->
                        if (next.toLong() != objective.progress) {
                            scope.launch { repos.objectives.updateProgress(objective.id, next.toLong()) }
                        }
                    },
                )
            }

            // La prochaine micro-tâche, seule et en grand (feuille de route v2).
            val allTasks by repos.tasks.observeAll().collectAsState(emptyList())
            val stepIds = milestones.map { it.id }
            val pendingTasks = remember(allTasks, stepIds) {
                allTasks
                    .filter { it.is_deleted == 0L && it.status != "done" && it.parent_id in stepIds }
                    .sortedWith(compareBy<Task> { stepIds.indexOf(it.parent_id) }.thenBy { it.order_index })
            }
            val nextTask = pendingTasks.firstOrNull()
            if (nextTask != null) {
                NextMicroTaskCard(
                    task = nextTask,
                    stepTitle = milestones.firstOrNull { it.id == nextTask.parent_id }?.title ?: "",
                    remaining = pendingTasks.size - 1,
                    onDone = { onToggleTask(nextTask) },
                    onFocus = onSendToPomodoro?.let { fn -> { fn(nextTask) } },
                )
            }

            ObjectivePastDeadlineBlock(objective = objective, repos = repos)

            if (!objective.description.isNullOrBlank()) {
                Text(
                    text = objective.description!!,
                    color = TeddTokens.InkMuted,
                    fontSize = 13.5.sp,
                    lineHeight = 20.sp,
                )
            }

            if (!objective.motivation.isNullOrBlank()) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(
                            Brush.linearGradient(
                                listOf(
                                    TeddTokens.LimeStrong.copy(alpha = 0.06f),
                                    TeddTokens.LimeStrong.copy(alpha = 0.06f),
                                )
                            )
                        )
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                        .padding(14.dp),
                    verticalAlignment = Alignment.Top,
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    TeddIcon(name = "spark", size = 14.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.dp)
                    Text(
                        text = "« ${objective.motivation!!} »",
                        color = TeddTokens.InkMuted,
                        fontSize = 13.sp,
                        fontStyle = FontStyle.Italic,
                    )
                }
            }

            // ── Étapes (repliées par défaut quand une micro-tâche est déjà proposée) ──
            var showSteps by remember(objective.id) { mutableStateOf(nextTask == null) }
            Column {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { showSteps = !showSteps }
                        .padding(bottom = 10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        text = "ÉTAPES",
                        color = TeddTokens.InkMuted,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        letterSpacing = 0.6.sp,
                        modifier = Modifier.weight(1f),
                    )
                    Text(
                        text = "${milestones.size} étape${if (milestones.size > 1) "s" else ""} · ${pendingTasks.size} micro-tâche${if (pendingTasks.size > 1) "s" else ""} restante${if (pendingTasks.size > 1) "s" else ""}",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                    )
                    Box(modifier = Modifier.size(22.dp).rotate(if (showSteps) 90f else 0f), contentAlignment = Alignment.Center) {
                        TeddIcon(name = "chevron", size = 13.dp, color = TeddTokens.InkMuted)
                    }
                }
                if (!showSteps) {
                    // replié
                } else if (milestones.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .padding(16.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = "Aucune étape pour l'instant.",
                                color = TeddTokens.InkMuted,
                                fontSize = 12.sp,
                            )
                            Spacer(modifier = Modifier.height(6.dp))
                            Text(
                                text = "Utilise « Modifier » pour découper l'objectif en étapes.",
                                color = TeddTokens.InkSubtle,
                                fontSize = 11.sp,
                            )
                        }
                    }
                } else {
                    val milestoneReorder = rememberReorderableState()
                    ReorderableColumn(
                        items = milestones,
                        keyOf = { it.id },
                        state = milestoneReorder,
                        onReorder = { from, to ->
                            scope.launch {
                                repos.milestones.reorderInObjective(objective.id, from, to)
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                    ) { index, ms, dragHandleModifier ->
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(bottom = 14.dp)
                                .clip(RoundedCornerShape(TeddTokens.RSm))
                                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                .padding(14.dp),
                        ) {
                            MilestoneBlock(
                                milestone = ms,
                                index = index,
                                repos = repos,
                                onToggleTask = onToggleTask,
                                onRequestDelete = { confirmDeleteMilestoneId = ms.id },
                                onSendToPomodoro = onSendToPomodoro,
                                dragHandleModifier = dragHandleModifier,
                                dropLock = milestoneReorder.recentlyDropped,
                            )
                        }
                    }
                }
            }
        }
    }

    // Confirmation suppression jalon (spec §8.4).
    if (confirmDeleteMilestoneId != null) {
        val ms = milestones.firstOrNull { it.id == confirmDeleteMilestoneId }
        if (ms != null) {
            ConfirmDeleteMilestoneDialog(
                title = ms.title,
                onCancel = { confirmDeleteMilestoneId = null },
                onConfirm = {
                    val id = ms.id
                    val objectiveId = objective.id
                    confirmDeleteMilestoneId = null
                    scope.launch {
                        repos.milestones.softDelete(id)
                        repos.progress.onMilestoneChanged(objectiveId)
                    }
                },
            )
        } else {
            confirmDeleteMilestoneId = null
        }
    }
}





@Composable
private fun ConfirmDeleteMilestoneDialog(
    title: String,
    onCancel: () -> Unit,
    onConfirm: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.42f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onCancel,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .width(380.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {},
                )
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text("Supprimer le jalon ?", color = TeddTokens.Ink, fontSize = 15.sp, fontWeight = FontWeight.SemiBold)
            Text(
                "« $title » et toutes ses actions seront perdus.",
                color = TeddTokens.InkMuted,
                fontSize = 12.sp,
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
            ) {
                TeddButton(
                    text = "Annuler",
                    onClick = onCancel,
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Md,
                )
                TeddButton(
                    text = "Supprimer",
                    onClick = onConfirm,
                    kind = TeddButtonKind.Danger,
                    size = TeddButtonSize.Md,
                )
            }
        }
    }
}

private fun parseHexDetail(hex: String): Long {
    val clean = hex.trimStart('#')
    val rgb = clean.toLong(16)
    return 0xFF000000L or rgb
}

/**
 * La micro-tâche suivante, seule et en grand : le premier pas, jamais la montagne.
 * Bloc d'encre, le seul élément sombre du panneau.
 */
@Composable
internal fun NextMicroTaskCard(
    task: Task,
    stepTitle: String,
    remaining: Int,
    onDone: () -> Unit,
    onFocus: (() -> Unit)?,
) {
    val minutes = task.estimated_minutes?.toInt() ?: 10
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RLg))
            .background(TeddTokens.Ink, RoundedCornerShape(TeddTokens.RLg))
            .padding(18.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Text(
            text = "PROCHAINE MICRO-TÂCHE · $minutes MIN",
            style = TeddType.Overline,
            color = TeddTokens.Bg.copy(alpha = 0.62f),
        )
        Text(text = task.title, style = TeddType.Title2, color = TeddTokens.Bg)
        if (stepTitle.isNotBlank()) {
            Text(text = stepTitle, color = TeddTokens.Bg.copy(alpha = 0.7f), fontSize = 12.5.sp)
        }
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            if (onFocus != null) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RSm))
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onFocus)
                        .height(36.dp)
                        .padding(horizontal = 14.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        TeddIcon(name = "focus", size = 13.dp, color = TeddTokens.Ink, strokeWidth = 2.2.dp)
                        Text("Faire maintenant", color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Bg.copy(alpha = 0.12f), RoundedCornerShape(TeddTokens.RSm))
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onDone)
                    .height(36.dp)
                    .padding(horizontal = 12.dp),
                contentAlignment = Alignment.Center,
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    TeddIcon(name = "check", size = 13.dp, color = TeddTokens.Bg, strokeWidth = 2.4.dp)
                    Text("Fait", color = TeddTokens.Bg, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                }
            }
            Spacer(modifier = Modifier.weight(1f))
            if (remaining > 0) {
                Text(
                    text = "$remaining autre${if (remaining > 1) "s" else ""} ensuite",
                    color = TeddTokens.Bg.copy(alpha = 0.55f),
                    fontSize = 11.5.sp,
                )
            }
        }
    }
}
